import { AsyncMessageCommunicator } from '@bblabs/async-comms';
import { Environment } from '@bblabs/module-library/constants';
import { REPLY_SENT_EVENT } from '@/apps/ReplyWithVideo/events';

/** Get basic popup window constraints for opening a new popup window. */
function getWindowFeatures() {
  const features: string[] = [];

  const width = 500;
  const height = 560;

  // center our window in the screen
  features.push(`width=${width}`);
  features.push(`height=${height}`);
  features.push(`top=${window.screen.availHeight / 2 - height / 2}`);
  features.push(`left=${window.screen.availWidth / 2 - width / 2}`);
  features.push('popup');

  return features.join(',');
}

type OpenRwvPopupOptions = {
  // Environment we are running in
  environment: Uppercase<Environment>;
  // Called when the popup window sent a reply
  replySent: (value: unknown) => Promise<void>;
  // Video ID the reply is coming from
  videoId: string;
  // Client ID of the video owner
  clientId: string;
  // Current video view id (used for reactions tracking)
  videoViewId: string;
  // Current browser viewer id (if any)
  browserViewerId: string;
  // Current bixel/job id (if any)
  beid: string;
};

export default async function openRwvPopup({
  environment,
  replySent,
  videoId,
  clientId,
  videoViewId,
  browserViewerId,
  beid,
}: OpenRwvPopupOptions): Promise<void> {
  let urlEnvironment = '';
  if (environment.toLowerCase() !== 'prod') {
    urlEnvironment = `${environment.toLowerCase()}.`;
  }

  const host = `https://${urlEnvironment}bbemaildelivery.com`;
  const url = new URL(`${host}/bbext/`);
  url.searchParams.set('p', 'rwv');
  url.searchParams.set('bbsource', 'rwv');
  url.searchParams.set('videoId', videoId);
  url.searchParams.set('clientId', clientId);
  url.searchParams.set('videoViewId', videoViewId);
  url.searchParams.set('browserViewerId', browserViewerId ?? '');
  url.searchParams.set('beid', beid ?? '');

  const windowFeatures = getWindowFeatures();
  const rwvWindow = window.open(url.toString(), '_blank', windowFeatures)!;

  // we're not using the "syncWithTarget" feature since we may navigate away to the
  // login page. because of that we'll set the origin ourselves since we know who we
  // should be communicating with.
  const communicator = new AsyncMessageCommunicator();
  communicator.attachListener(window);
  communicator.setTargetWindow(rwvWindow);
  communicator.addHandler(REPLY_SENT_EVENT, replySent);
  communicator.setOrigin(host);

  return new Promise((resolve) => {
    // Should we reject if window is null?
    const id = setInterval(() => {
      if (rwvWindow.closed) {
        clearInterval(id);
        resolve();
      }
    }, 50);
  });
}
